import { Box, Button, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { documentsList } from "../assets/documentsList";
import { Footer } from "../components/Footer";
import { DNA } from "react-loader-spinner";

export function SupportingDocumentation({
  claimId,
  email,
  handleNext,
  handleBack,
  activeStep,
  steps = ["Supporting Documentation"],
  totalFormData = [],
  handleDataSubmitted,
  isDocumentUploadOnly,
}: {
  claimId?: string;
  isDocumentUploadOnly?: boolean;
  email?: string;
  handleNext?: () => void;
  handleBack?: () => void;
  handleSkip?: () => void;
  activeStep?: number;
  isStepOptional?: (step: number) => boolean;
  steps?: string[];
  totalFormData?: any;
  handleDataSubmitted?: (
    data: { fileName: string; file: string }[],
    step: string
  ) => void;
}) {
  const [uploadedDocuments, setUploadedDocuments] = useState<
    { fileName: string; file: string }[]
  >(totalFormData["supporting-documentation"] || []);
  const [submittedDocuments, setSubmittedDocuments] = useState<string>("");
  const [allDocuments, setAllDocuments] = useState<string[]>([]);
  const [previousDocuments, setPreviousDocuments] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [claimDocumentsOnlySuccessModal, setClaimDocumentsOnlySuccessModal] =
    useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isDocumentUploadOnly) {
      const documentsRequired = totalFormData["claim-info"] || {};
      const allDocs = documentsList.reduce((acc: string[], item) => {
        const keys = Object.keys(documentsRequired);
        if (keys.includes(item.name)) {
          acc.push(...item.documents);
        }
        return acc;
      }, []);
      // Always require these base documents
      const baseRequired = [
        "Service provider's invoice/ Doctors account",
        "Hospital bill",
        "Medical scheme statement",
      ];
      // Deduplicate and enforce base requirements
      const uniqueSet = new Set<string>(allDocs);
      baseRequired.forEach((d) => uniqueSet.add(d));

      // If user selected other bank details, require proof of banking details
      const bankingDetails = totalFormData["banking-details"] || {};
      if (bankingDetails.refundAccountOption === "Other bank details") {
        uniqueSet.add("Proof of banking details");
      }

      const uniqueDocuments = Array.from(uniqueSet);
      setAllDocuments(uniqueDocuments);
      totalFormData["supporting-documentation-required"] = uniqueDocuments;

      if (totalFormData["supporting-documentation"]) {
        const { "submitted-documents": documents } =
          totalFormData["supporting-documentation"];
        setSubmittedDocuments(documents || "");
      }
    }
  }, [totalFormData, claimId]);

  useEffect(() => {
    if (isDocumentUploadOnly) {
      setIsLoading(true);
      fetch(
        `https://siragoapiprod.azure-api.net/siragoGap?cmd=getPreviousDocs&claimId=${claimId}`
      )
        .then((res) => res.json())
        .then((data) => {
          setAllDocuments(data.requiredDocuments);
          setPreviousDocuments(data.previousUploadedDocNames);
          setIsLoading(false);
        });
    }
  }, [claimId, claimDocumentsOnlySuccessModal]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const files = Array.from(event.target.files);
      files.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          setUploadedDocuments((prevDocuments) => [
            ...prevDocuments,
            {
              fileName: file.name,
              file: base64String,
            },
          ]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    // Require at least 1 document to be uploaded
    if (uploadedDocuments.length < 1) {
      errors["uploaded-documents"] = "Please upload at least one document.";
    }
    return errors;
  };

  const handleClaimDocumentSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);
    await fetch(
      `https://siragoapiprod.azure-api.net/siragoGap?cmd=uploadClaimDocumentsOnly`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          documents: uploadedDocuments,
          submittedDocuments,
          claimId,
          email,
        }),
      }
    )
      .then((e) => {
        setIsLoading(false);
        e.json().then((data) => {
          if (data.successMessage) {
            setClaimDocumentsOnlySuccessModal(true);
          }
        });
      })
      .catch((e) => {
        setIsLoading(false);
      });
  };

  const removeUploadedDocument = (index: number) => {
    setUploadedDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    let documents: { fileName: string; file: string }[] = [];
    uploadedDocuments.forEach((document) => {
      documents.push({
        fileName: document.fileName,
        file: document.file,
      });
    });

    handleDataSubmitted &&
      handleDataSubmitted(documents, "supporting-documentation");

    // advance to next step in the claim flow
    if (!isDocumentUploadOnly && handleNext) {
      handleNext();
    }
  };

  return (
    <>
      {isLoading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <DNA
            visible={true}
            height="300"
            width="200"
            ariaLabel="line-wave-loading"
            wrapperStyle={{}}
            wrapperClass=""
          />
        </div>
      )}
      {!isLoading && (
        <div
          style={{
            marginLeft: isDocumentUploadOnly ? "1em" : "0",
            marginTop: isDocumentUploadOnly ? "5rem" : "0",
          }}
        >
          <Box sx={{ width: "100%", mb: 2 }}>
            <h4 style={{ borderBottom: "1px solid #ccc", margin: "1em 0" }}>
              Supporting Documentation
            </h4>
            <h5>Upload the following supporting documentation</h5>
            <div>
              {allDocuments.map((doc) => (
                <p style={{ fontSize: "14px", marginBottom: "0" }}>*{doc}</p>
              ))}
            </div>

            <input
              accept="image/*,application/pdf"
              style={{ display: "none" }}
              id="raised-button-file"
              type="file"
              onChange={handleFileUpload}
              multiple
            />
            <label htmlFor="raised-button-file" style={{ marginTop: "1em" }}>
              <Button variant="contained" component="span">
                Upload Documents
              </Button>
            </label>
            {uploadedDocuments.length > 0 && (
              <p>{uploadedDocuments.length} file(s) uploaded successfully</p>
            )}
            {uploadedDocuments.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {uploadedDocuments.map((doc, idx) => (
                  <Box
                    key={`${doc.fileName}-${idx}`}
                    sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                  >
                    <Typography sx={{ mr: 1, fontSize: "14px" }}>
                      {doc.fileName}
                    </Typography>
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => removeUploadedDocument(idx)}
                    >
                      Remove
                    </Button>
                  </Box>
                ))}
              </Box>
            )}
            {formErrors["uploaded-documents"] && (
              <Typography color="error">
                {formErrors["uploaded-documents"]}
              </Typography>
            )}
          </Box>
          {isDocumentUploadOnly && (
            <Box sx={{ width: "100%", mb: 2 }}>
              <h4 style={{ borderBottom: "1px solid #ccc", margin: "1em 0" }}>
                Previously Uploaded Documents
              </h4>
              {previousDocuments.map((doc) => (
                <p style={{ fontSize: "14px", marginBottom: "0" }}>{doc}</p>
              ))}
            </Box>
          )}
        </div>
      )}

      {!isDocumentUploadOnly && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            py: 2,
            mb: "10em",
          }}
        >
          <Button
            color="inherit"
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ mr: 1, fontSize: "1.2em" }}
          >
            Back
          </Button>
          <Box sx={{ flex: "1 1 auto" }} />

          <Button onClick={handleSubmit} sx={{ fontSize: "1.2em" }}>
            {activeStep === steps!.length - 1 ? "Finish" : "Next"}
          </Button>
        </Box>
      )}
      {isDocumentUploadOnly && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            py: 2,
            mb: "15em",
          }}
        >
          <Button
            onClick={handleClaimDocumentSubmit}
            sx={{ fontSize: "1.2em", ml: 2, mb: 10 }}
          >
            Submit
          </Button>
        </Box>
      )}

      {claimDocumentsOnlySuccessModal && (
        <div
          style={{
            position: "absolute",
            zIndex: "100",
            margin: "2em",
            width: "60vw",
            marginLeft: "auto",
            marginRight: "auto",
            left: 0,
            right: 0,
            top: 50,
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              flexDirection: "column",
              boxShadow: 24,
              background: "white",
              border: "1px solid #d3d3d3",
              borderRadius: "10px",
              padding: "2em",
            }}
          >
            <img
              style={{
                height: "auto",
                width: "40vw",
                padding: "1em",
                margin: "auto",
              }}
              src={require("../assets/success.gif")}
              alt="success..."
            />

            <Typography sx={{ mt: 2, mb: 1, textAlign: "center" }}>
              Documents uploaded successfully
            </Typography>
            <Button
              variant="contained"
              onClick={() => (window.location.href = "https://sirago.co.za/")}
              sx={{ fontSize: "1.2em", ml: 2, mb: 2, margin: "auto" }}
            >
              Continue
            </Button>
          </Box>
        </div>
      )}
      <Footer />
    </>
  );
}
